import { Button } from "@/components/ui/button";
import { useI18n } from "@/hooks/useI18n";
import { useDrawers } from "@/hooks/useDrawers";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Truck, Shield, Clock, Globe } from "lucide-react";
import { useModernImage } from "@/hooks/useModernImage";
import heroImageWebp from "@/assets/hero-flowers-optimized.webp";
import heroImageJpeg from "@/assets/hero-flowers-optimized.jpg";

export const Hero = () => {
  const { t } = useI18n();
  const { openRFQ } = useDrawers();
  const navigate = useNavigate();
  const heroImage = useModernImage({ webp: heroImageWebp, fallback: heroImageJpeg });

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Priority LCP image - invisible but loads first */}
      <img 
        src={heroImage}
        alt="Premium Ecuadorian flowers for Dubai export"
        className="absolute inset-0 w-full h-full object-cover opacity-0 pointer-events-none"
        loading="eager"
        fetchPriority="high"
      />
      
      {/* Hero background image with priority loading */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url(${heroImage})`,
          willChange: 'transform'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-andean-forest/80 to-andean-forest/40"></div>
      </div>
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-andean rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-gradient-gold rounded-full blur-3xl"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-andean-sage rounded-full blur-2xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-4xl mx-auto">
          {/* Main heading */}
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-light text-andean-cream mb-6 animate-fade-up text-shadow tracking-wide leading-tight">
            {t('hero.title')}
          </h1>
          
          {/* Subtitle */}
          <p className="text-lg md:text-xl font-light text-andean-cream/90 mb-4 animate-fade-up tracking-wide">
            {t('hero.subtitle')}
          </p>

          {/* Description */}
          <p className="text-base text-andean-cream/80 max-w-2xl mx-auto mb-12 animate-fade-up font-light leading-relaxed">
            We connect top Ecuadorian farms with premium buyers
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16 animate-fade-up">
            <Button 
              variant="accent" 
              size="lg" 
              className="h-12 px-6 text-base font-light"
              onClick={() => navigate('/catalog')}
            >
              {t('hero.cta.catalog')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button 
              variant="hero" 
              size="lg" 
              className="h-12 px-6 text-base font-light"
              onClick={openRFQ}
            >
              {t('hero.cta.quote')}
            </Button>
          </div>

          {/* Trust indicators */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 animate-scale-in">
            {[
              { icon: Truck, key: 'trust.coldchain' },
              { icon: Shield, key: 'trust.qc' },
              { icon: Clock, key: 'trust.ontime' },
              { icon: Globe, key: 'trust.ecuador' },
            ].map(({ icon: Icon, key }) => (
              <div key={key} className="bg-andean-cream/95 backdrop-blur-sm rounded-lg p-4 shadow-soft text-center">
                <Icon className="w-6 h-6 text-accent mx-auto mb-2" />
                <p className="text-xs font-light text-primary tracking-wide">{t(key)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};